import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

export default function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page">
      <Navbar />
      <SEOHead title="Privacy Policy" description="ChannelLoyalty privacy policy. How we collect, use, and protect your data on our B2B channel loyalty platform." canonical="https://channelloyalty.ai/privacy-policy" />
      
      <main>
        <section className="section pt-32">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy for ChannelLoyalty.ai</h1>
              
              <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                <p>
                  Welcome to ChannelLoyalty.ai (the "Website"). TagQuest Innovations Private Limited (company registration number: 08046168, registered office: Building 18, Commercial Complex, opposite CALA Restaurant & Bar, Panchsheel Park, New Delhi, Delhi, 110017) (hereinafter referred to as "ChannelLoyalty", "ChannelLoyalty.ai", "We", "Us", "Our") takes privacy seriously. Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Website. By using the Website, you agree to the terms of this Privacy Policy.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">Introduction</h2>
                <p>
                  ChannelLoyalty provides a hosted Software-as-a-Service (SaaS) platform designed for Clients (businesses using our Loyalty & Engagement Platform) that have entered into agreements with TagQuest Innovations Private Limited for user data orchestration and engagement. Our Clients include partners and resellers who integrate our Services with their own offerings, typically through websites, apps, or social media channels.
                </p>
                <p>
                  ChannelLoyalty enables Clients to gain insights into their Consumers (end-users of the Loyalty Platform) and deliver personalized offers, campaigns, and loyalty programs. Our platform offers a suite of tools for creating reward programs, member offers, surveys, seasonal campaigns, and audience segments based on Consumer data.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">1. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6">a. Personal Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Identifiers:</strong> Name, email address, phone number, and other contact details.</li>
                  <li><strong>Payment Information:</strong> Credit card details, billing address, and transaction history (if applicable).</li>
                  <li><strong>Account Information:</strong> Username, password, and other credentials used to access your account.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6">b. Non-Personal Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Usage Data:</strong> IP address, browser type, operating system, pages visited, and time spent on the Website.</li>
                  <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to enhance your experience and analyze Website traffic.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6">c. Information from Third Parties</h3>
                <p>We may receive information about you from third-party services, such as payment processors or analytics providers.</p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">2. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To provide, operate, and maintain the Website.</li>
                  <li>To process transactions and send transaction notifications.</li>
                  <li>To improve, personalize, and optimize your experience on the Website.</li>
                  <li>To communicate with you, including responding to inquiries and sending updates.</li>
                  <li>To analyze usage trends and monitor the effectiveness of our marketing efforts.</li>
                  <li>To comply with legal obligations and protect our rights.</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">3. How We Share Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Service Providers:</strong> With third-party vendors who assist us in operating the Website and providing services.</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights, property, or safety.</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information.</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">4. Data Security</h2>
                <p>
                  We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">5. Your Privacy Rights</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
                  <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information.</li>
                  <li><strong>Deletion:</strong> Request that we delete your personal information.</li>
                  <li><strong>Opt-Out:</strong> Opt-out of receiving marketing communications or the use of cookies.</li>
                </ul>
                <p>To exercise these rights, please contact us at <a href="mailto:chandra@channelloyalty.ai" className="text-[#2C39B8] hover:underline">chandra@channelloyalty.ai</a>.</p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">6. Third-Party Links</h2>
                <p>
                  The Website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies before providing any personal information.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">7. Children's Privacy</h2>
                <p>
                  The Website is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected such information, we will take steps to delete it.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">8. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Your continued use of the Website after such changes constitutes your acceptance of the revised Privacy Policy.
                </p>
                <p><strong>This Privacy Notice enters into force on February 10, 2025.</strong></p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">9. Contact Us</h2>
                <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>
                <ul className="list-none space-y-2">
                  <li><strong>Email:</strong> <a href="mailto:chandra@channelloyalty.ai" className="text-[#2C39B8] hover:underline">chandra@channelloyalty.ai</a></li>
                  <li><strong>Address:</strong> Building 18, Commercial Complex, opposite CALA Restaurant & Bar, Panchsheel Park, New Delhi, Delhi, 110017</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
